import { useConfig } from "@/context/ConfigContext";
import { useEffect, useState } from "react";

/**
 * Hook untuk mengambil data lokasi (provinces, cities, districts, subdistricts)
 * @param {Object} options - Options untuk fetch
 * @param {string} options.type - Type lokasi: 'provinces', 'cities', 'districts', 'subdistricts'
 * @param {string} options.province_code - Filter by province code (untuk cities)
 * @param {string} options.city_code - Filter by city code (untuk districts)
 * @param {string} options.district_code - Filter by district code (untuk subdistricts)
 * @param {boolean} options.enabled - Enable/disable fetch (default: true)
 */
export function useLocations(options = {}) {
  const { type, province_code, city_code, district_code, enabled = true } = options;
  const { config, configData } = useConfig();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !configData?.team_id || !config?.api?.baseUrl || !type) {
      return;
    }

    const fetchLocations = async () => {
      try {
        setLoading(true);
        setError(null);

        const teamId = configData.team_id;

        // Build query params
        const queryParams = new URLSearchParams();
        if (province_code) queryParams.append("province_code", province_code);
        if (city_code) queryParams.append("city_code", city_code);
        if (district_code) queryParams.append("district_code", district_code);

        const queryString = queryParams.toString();

        const proxyUrl = `/api/checkout?team_id=${teamId}&action=locations&type=${type}${
          queryString ? `&${queryString}` : ""
        }`;

        console.log("[useLocations] Fetching locations:", {
          proxyUrl,
          teamId,
          type,
          province_code,
          city_code,
          district_code,
        });

        const response = await fetch(proxyUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          cache: "no-cache",
        });

        if (!response.ok) {
          let errorData;
          let errorText;

          try {
            errorText = await response.text();
            errorData = JSON.parse(errorText);
          } catch (parseError) {
            errorData = {
              message: errorText || response.statusText,
              status: response.status,
            };
          }

          console.error("[useLocations] API Error:", errorData);
          throw new Error(
            errorData.message ||
              errorData.error ||
              errorText ||
              response.statusText
          );
        }

        const data = await response.json();
        setLocations(data.data || data || []);
      } catch (err) {
        console.error("[useLocations] Error fetching locations:", {
          message: err.message,
          stack: err.stack,
          teamId: configData?.team_id,
          type,
        });
        setError(err.message || "Gagal mengambil data lokasi");
      } finally {
        setLoading(false);
      }
    };

    fetchLocations();
  }, [
    configData?.team_id,
    config?.api?.baseUrl,
    type,
    province_code,
    city_code,
    district_code,
    enabled,
  ]);

  return {
    locations,
    loading,
    error,
  };
}

